// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { createContext, useContext, useState, type ReactNode } from 'react'
import type { Patient } from '@/types'
import type { ParsedRow } from '@/lib/parse-csv'

interface PatientStore {
  patients: Patient[]
  importPatients: (rows: ParsedRow[]) => number
  updatePatient: (id: string, changes: Partial<Pick<Patient, 'prepStatus'>>) => void
}

const PatientStoreContext = createContext<PatientStore | null>(null)

export function PatientStoreProvider({ children }: { children: ReactNode }) {
  // Starts empty — no seed data. Bulk CSV import populates it in-memory; live
  // patient lists/details come from the API, not this store.
  const [patients, setPatients] = useState<Patient[]>([])

  function importPatients(rows: ParsedRow[]): number {
    const existing = new Set(patients.map(p => `${p.name}|${p.dob}`))
    const newPatients: Patient[] = rows
      .filter(r => !existing.has(`${r.name}|${r.dob}`))
      .map(r => ({
        id: crypto.randomUUID(),
        name: r.name,
        dob: r.dob,
        provider: r.provider || 'No provider',
        appointmentDateTime: r.appointmentDateTime,
        prepStatus: 'unopened' as const,
        hasActiveConversation: false,
      }))
    setPatients(prev => [...prev, ...newPatients])
    return newPatients.length
  }

  function updatePatient(id: string, changes: Partial<Pick<Patient, 'prepStatus'>>) {
    setPatients(prev => prev.map(p => p.id === id ? { ...p, ...changes } : p))
  }

  return (
    <PatientStoreContext.Provider value={{ patients, importPatients, updatePatient }}>
      {children}
    </PatientStoreContext.Provider>
  )
}

export function usePatients(): PatientStore {
  const ctx = useContext(PatientStoreContext)
  if (!ctx) throw new Error('usePatients must be used within PatientStoreProvider')
  return ctx
}
